import React, { useEffect, useState } from "react";
import { Snowflake } from "lucide-react";
import { BACKEND_URL } from "../lib/backendUrl";

/**
 * Soft "AI cooling down…" pill.
 *
 * Polls `/api/llm/status` every 5 seconds (fast 1s tick once cooling starts so
 * the countdown feels live) and renders a tiny floating chip in the bottom-left
 * corner whenever the GLM rate-limiter is in cooldown. The pill auto-hides
 * once the bucket refills, so users never have to interact with it.
 */
export default function AICoolingPill() {
  const [cooling, setCooling] = useState(false);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const API = BACKEND_URL;
    let alive = true;
    let timer = null;

    async function poll() {
      try {
        const res = await fetch(`${API}/api/llm/status`, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        const cool = !!data.cooling_down;
        setCooling(cool);
        setRemaining(Math.max(0, Number(data.retry_in_seconds) || 0));
      } catch (_) {
        // Network errors are silent — the pill simply stays hidden.
      }
    }

    function schedule() {
      // Fast tick while cooling, slow tick when calm.
      const ms = cooling ? 1000 : 5000;
      timer = setTimeout(async () => {
        await poll();
        schedule();
      }, ms);
    }

    poll().then(schedule);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
    // Re-evaluate the tick interval whenever `cooling` flips.
  }, [cooling]);

  if (!cooling) return null;

  return (
    <div
      data-testid="ai-cooling-pill"
      className="fixed bottom-5 left-5 z-[80] flex items-center gap-2 rounded-full
                 bg-slate-900/90 px-3.5 py-2 text-xs font-medium text-slate-100
                 shadow-lg ring-1 ring-white/10 backdrop-blur-sm
                 animate-[fadeIn_0.25s_ease-out]"
    >
      <span className="relative flex h-4 w-4 items-center justify-center">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400/60" />
        <Snowflake className="relative h-3.5 w-3.5 text-sky-300" />
      </span>
      <span>
        AI cooling down…
        {remaining > 0 && (
          <span className="ml-1 tabular-nums text-slate-300">{remaining}s</span>
        )}
      </span>
    </div>
  );
}
